"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Search, FileText, Settings, FileCheck, Shield, Archive, Layout } from "lucide-react";
import { cn } from "@/lib/utils";

const DOCUMENT_TYPES = [
  { id: "policy", name: "Policy", description: "Official organisational policies", icon: Shield },
  { id: "procedure", name: "Procedure", description: "Step-by-step process instructions", icon: Settings },
  { id: "form", name: "Form", description: "Templates and data collection forms", icon: Layout },
  { id: "standard", name: "Standard", description: "Industry and compliance standards", icon: FileCheck },
  { id: "guideline", name: "Guideline", description: "Best practices and recommendations", icon: FileText },
  { id: "record", name: "Record", description: "Historical documentation and logs", icon: Archive },
  { id: "template", name: "Template", description: "Reusable document templates", icon: Layout }
];

export default function ModalListPickerExample() {
  const [open, setOpen] = React.useState(true);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [query, setQuery] = React.useState("");

  const toggle = (id: string) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const filtered = React.useMemo(() => 
    DOCUMENT_TYPES.filter(item => 
      item.name.toLowerCase().includes(query.toLowerCase()) ||
      item.description.toLowerCase().includes(query.toLowerCase())
    ), [query]
  );

  const toggleAll = () => {
    if (selected.length === filtered.length) {
      setSelected([]);
    } else {
      setSelected(filtered.map(item => item.id));
    }
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open List Picker Modal</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-7xl! w-7xl! h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col">
            {/* Header */}
            <div className="p-6 pb-4 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent">
              <h2 className="text-3xl font-bold text-foreground">Select Document Types</h2>
              <p className="mt-2 text-muted-foreground">Choose the document types you want to include in your compliance framework</p>
              
              <div className="mt-3 flex items-center gap-4 text-sm">
                <Badge variant="outline">{selected.length} selected</Badge>
                <Badge variant="secondary">{DOCUMENT_TYPES.length} total types</Badge>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              {/* Search and Actions */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <div className="relative flex-1 sm:max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Command className="w-full">
                    <CommandInput 
                      placeholder="Search document types..." 
                      value={query}
                      onValueChange={setQuery}
                      className="pl-10 bg-white/10 border-white/20"
                    />
                  </Command>
                </div>
                <button 
                  onClick={toggleAll}
                  className="text-sm text-primary hover:underline"
                  type="button"
                >
                  {selected.length === filtered.length ? 'Deselect all' : 'Select all'}
                </button>
              </div>

              {/* Document Types Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((item) => {
                  const Icon = item.icon;
                  const isSelected = selected.includes(item.id);
                  
                  return (
                    <Card 
                      key={item.id}
                      className={cn(
                        "cursor-pointer transition-all duration-200 hover:shadow-lg border-2",
                        isSelected 
                          ? "border-primary bg-primary/10 shadow-md" 
                          : "border-white/20 bg-white/5 hover:border-primary/50"
                      )}
                      onClick={() => toggle(item.id)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start gap-3">
                          <div className={cn(
                            "p-2 rounded-lg transition-colors",
                            isSelected ? "bg-primary text-primary-foreground" : "bg-white/10 text-muted-foreground"
                          )}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-lg leading-tight">{item.name}</CardTitle>
                            <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                          </div>
                          {isSelected && (
                            <Check className="h-5 w-5 text-primary shrink-0" />
                          )}
                        </div>
                      </CardHeader>
                    </Card>
                  );
                })}
              </div>

              {/* Selected Items Summary */}
              {selected.length > 0 && (
                <Card className="bg-white/5 border-white/20">
                  <CardHeader>
                    <CardTitle className="text-lg">Selected Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {selected.map(id => {
                        const item = DOCUMENT_TYPES.find(x => x.id === id);
                        if (!item) return null;
                        return (
                          <Badge key={id} variant="outline" className="text-sm">
                            {item.name}
                          </Badge>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 px-6 py-4 bg-linear-to-t from-primary/5 to-transparent flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {selected.length > 0 ? (
                  <span className="text-green-600">
                    {selected.length} document type{selected.length !== 1 ? 's' : ''} selected
                  </span>
                ) : (
                  <span>Select at least one document type to continue</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button disabled={selected.length === 0} onClick={() => setOpen(false)}>
                  Apply Selection
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
